import { AxiosError } from 'axios'
import { ZodIssue } from 'zod'
import { DEFAULT_INTERNAL_SYSTEM, ERROR_CODES, ErrorCode, LOG_SOURCE_LABELS } from './ErrorCodes'

export interface GenericError extends Error {
  extensions?: {
    contentful: {
      code: string
      requestId: string
    }
  }
  path?: string[]
  code?: string
  status?: number
}

export interface GenericExceptionError extends Partial<ErrorCode> {
  message?: string
  /** Used to note any downstream error objects eg. Axios, JRT and Auth0 */
  originalError?: GenericError
  /** Used to note any validation errors coming from zod schemas, in relation to any 422s */
  validationErrors?: ZodIssue[]
  responseData?: AxiosError
  /** The system where the error occurred */
  system?: string
}

export class GenericException extends AxiosError {
  public httpStatus: number

  public code: string

  public kind?: string

  public subtype?: string

  public message: string

  public businessMessage: string

  public developerMessage: string

  public originalError?: GenericError

  public unrecoverable: boolean

  public validationErrors?: ZodIssue[]

  public responseData?: AxiosError

  public description?: string

  public system: string

  constructor(props: GenericExceptionError = {}) {
    super()
    const {
      httpStatus,
      message,
      code,
      kind,
      subtype,
      originalError,
      businessMessage,
      developerMessage,
      validationErrors,
      unrecoverable,
      responseData,
      description,
      system,
    } = props || {}
    this.stack = originalError?.stack
    this.businessMessage = businessMessage || ERROR_CODES.FATAL.businessMessage
    // Message is a fallback since it is present in original error
    this.developerMessage = developerMessage || message || ERROR_CODES.FATAL.developerMessage
    this.message = this.businessMessage
    this.code = code || ERROR_CODES.FATAL.code
    this.kind = kind
    this.subtype = subtype
    this.httpStatus = httpStatus || 500
    this.validationErrors = validationErrors
    this.unrecoverable = unrecoverable || false
    this.responseData = responseData
    this.description = description
    this.system = system || DEFAULT_INTERNAL_SYSTEM
    this.originalError = originalError
  }

  private getDownstreamError() {
    if (this.originalError instanceof AxiosError && this.originalError.response?.data) {
      return {
        message: this.originalError.message,
        ...this.originalError.response.data,
      }
    }

    return {
      ...(this.originalError?.message && {
        message: this.originalError?.message,
      }),
      ...(this.originalError?.code && {
        kind: this.originalError?.code,
      }),
      ...(this.originalError?.name && {
        description: this.originalError?.name,
      }),
      ...(this.originalError?.status && {
        httpStatus: this.originalError?.status,
      }),
      ...(this.responseData?.message &&
        this.validationErrors === undefined && {
          message: this.responseData?.message,
        }),
      ...(this.responseData?.status && {
        httpStatus: this.responseData?.status,
      }),
    }
  }

  /** fields used in datadog facets documented here https://aviosgroup.atlassian.net/wiki/spaces/RP/pages/2726625597/Configuring+Logs */
  public formatErrorForLogs(source: keyof typeof LOG_SOURCE_LABELS) {
    const errorMessage = this.developerMessage
    const downstreamError = this.getDownstreamError()
    const errorCode = downstreamError?.code ? downstreamError.code : this.code

    const message = `[${LOG_SOURCE_LABELS[source]} Error] '${errorCode}' - ${errorMessage}`

    return {
      message,
      level: this.httpStatus < 500 && this.httpStatus >= 400 ? 'warn' : 'error',
      error: {
        message: errorMessage,
        stack: this.stack,
        code: this.code,
        kind: this.kind,
        subtype: this.subtype,
        description: this.description,
        httpStatus: this.httpStatus,
        system: this.system,
        downstreamError,
        ...(this.validationErrors && { validationErrors: this.validationErrors }),
      },
    }
  }

  public getErrorResponse() {
    return {
      code: this.code,
      kind: this.kind,
      subtype: this.subtype,
      businessMessage: this.businessMessage,
      developerMessage: this.developerMessage,
      validationErrors: this.validationErrors,
      unrecoverable: this.unrecoverable,
    }
  }
}
